import { useState, lazy, Suspense } from "react";
import { WaitlistForm } from "@/components/waitlist-form";
// import VideoOverlay from "@/components/video-overlay";
// import CombinedShapeMask from "@/components/CombinedShapeMask";
import { Link } from "wouter";
import { useTranslations } from "@/hooks/use-translations";
import { useLocale } from "@/components/locale-provider";
import SEOHead from "@/components/seo-head";
import ResponsiveImage from "@/components/responsive-image";
import WhatsAppShareButton from "@/components/whatsapp-share-button";

// Lazy load below-the-fold components
const VideoOverlay = lazy(() => import("@/components/video-overlay"));

export default function Home() {
  const [showVideo, setShowVideo] = useState(false);
  const { locale } = useLocale();
  const { t } = useTranslations(locale);

  const handlePlayVideo = () => {
    setShowVideo(true);
  };

  return (
    <>
      <SEOHead />
      <main
        className="
        bg-[#301c36]
        w-full
        relative
        min-h-[100dvh]
        md:grid
        md:grid-col-1
        md:grid-rows-[auto_max-content]"
      >
      <div
        className="
        grid
        grid-cols-1
        md:grid-cols-2
        md:grid-cols-[5fr_6fr]
        relative"
      >
        <div
          className="
            w-[99.12px]
            absolute
            z-10
            top-10
            w-full
            flex
            justify-center
            md:block
            md:left-10
            md:w-auto
            "
          id="28x-logo"
        >
          <ResponsiveImage
            src="/assets/upper-left-28x-logo.svg"
            alt="28X Logo"
            className="w-[70px] md:w-[99px]"
            priority={true}
            lazy={false}
          />
        </div>

        <div className="relative" id="video">
          <div
            className="
            w-full
            md:max-w-[60%]
            inline-block
            relative"
          >
            <ResponsiveImage
              src="/assets/video-placeholder-image.png"
              alt="Phone video preview"
              className="
                w-full
                h-[375px]
                object-cover
                overflow-hidden
                rounded-bl-[9999rem]
                rounded-br-[9999rem]
                md:overflow-unset
                md:rounded-bl-unset
                md:rounded-br-unset
                md:object-unset
                md:h-full
                md:max-h-[90dvh]"
              sizes="(max-width: 768px) 100vw, 60vw"
              widths={[375, 768, 1024, 1440]}
              priority={true}
            />

            <div
              className="
                  hidden
                  md:block
                  absolute
                  bottom-[5%]
                  right-[-60%]
                  w-[70%]"
            >
              <img
                src="/assets/iphone-mockup.png"
                alt="iPhone App Preview"
                className="object-contain"
              />
            </div>

            <div
              className="
              absolute
              inset-0
              flex
              items-center
              justify-center"
            >
              <div
                className="
                    bg-[#c39d63]
                    w-20
                    h-20
                    flex
                    items-center
                    justify-center
                    cursor-pointer
                    hover:bg-[#b38d53]
                    transition-colors
                    z-10
                    rounded-full"
                onClick={handlePlayVideo}
              >
                <img
                  src="/assets/play-button.svg"
                  alt="Play Video"
                  className="
                    w-[75px]
                    h-[75px]
                    object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          className="
          md:flex
          md:flex-col
          justify-center
          w-full
          py-[54px]
          "
        >
          <div
            className="
            max-w-[640px]
            mx-auto
            w-full
            px-6"
          >
            <div
              className="
              relative
              pt-[32px]
              w-full"
            >
              <div
                className="
                  absolute
                  top-[-30px]
                  lg:top-[-50px]
                  right-[-20px]
                  hidden
                  md:block
                  opacity-50
                  "
                id="butterfly-graphic"
              >
                <img
                  src="/assets/upper-right-butterfly-logo.svg"
                  alt="Decorative Butterfly"
                  className="w-[90px] lg:w-[180px]"
                />
              </div>

              <div className="w-full">
                <p
                  className="
                  text-[80px]
                  md:text-[90px]
                  lg:text-[116px]
                  font-serifbold
                  leading-[80px]
                  lg:leading-[100px]
                  text-[#dfd968]"
                >
                  {t("home").hero.privateTitle}
                </p>
                <p
                  className="
                  text-[80px]
                  md:text-[90px]
                  lg:text-[116px]
                  font-serifbold
                  leading-[80px]
                  lg:leading-[100px]
                  text-right
                  text-[#dfd968]"
                >
                  {t("home").hero.periodTitle}
                </p>
              </div>

              <p
                className="
                text-[24px]
                md:text-[32px]
                lg:text-[42px]
                italic
                font-lightitalic
                text-right
                text-[#f1eebc]"
              >
                {t("home").hero.subtitle}
              </p>

              <div
                className="
                pt-[32px]
                md:pt-[80px]
                lg:pt-[100px]
                md:flex-col
                md:space-y-6
                md:items-center
                md:justify-start
                w-full"
              >
                <div
                  className="
                  flex
                  flex-col
                  items-start
                  justify-center
                  relative"
                >
                  {/* Removed call to action text */}
                  <WaitlistForm onPlayVideo={handlePlayVideo} />
                </div>

                <div className="flex flex-col space-y-2 items-center justify-start mt-[24px]">
                  <div className="flex items-center space-x-3 justify-center h-8">
                    <button 
                      className="bg-[#573262] hover:bg-[#472251] w-8 h-8 p-1.5 rounded-md flex items-center justify-center"
                      onClick={(e) => {
                        e.preventDefault();
                        const shareText = t("social").homeShareText;
                        const shareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                        window.open(shareUrl, "_blank");
                      }}
                    >
                      <img src="/assets/whatsapp.svg" alt="Share on WhatsApp" className="w-full h-full" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="
        w-full
        px-[16px]
        py-[32px]
        space-y-[32px]
        items-center
        justify-between
        self-end
        md:inline-flex
        md:flex-row
        md:space-y-0
        md:gap-x-[40px]
        md:px-[32px]
        lg:px-[40px]
        md:bg-[#c2bcac]
        md:bg-opacity-25
        md:py-8
        "
      >
        <div
          className="
            md:hidden
            opacity-50
            pb-[32px]"
          id="butterfly-graphic"
        >
          <img
            src="/assets/upper-right-butterfly-logo.svg"
            alt="Decorative Butterfly"
            className="w-[70%] mx-auto"
          />
        </div>

        <p className="text-[24px] md:text-[32px] font-serifbold tracking-[0.32px] leading-tight text-center text-[#f4eadf] uppercase">
          {t("home").footer.dataControl}
        </p>
        <hr className="max-w-[32px] h-px bg-[#F4EADF] border-0 mx-auto opacity-50 md:hidden" />
        <p className="text-[24px] md:text-[32px] italic tracking-[0.32px] leading-tight text-center text-[#f4eadf]">
          {t("home").footer.charitySupport}
        </p>
        <hr className="max-w-[32px] h-px bg-[#F4EADF] border-0 mx-auto opacity-50 md:hidden" />
        <p className="text-[24px] md:text-[32px] font-serifbold tracking-[0.32px] leading-tight text-center text-[#f4eadf] uppercase">
          {t("home").footer.freeForever}
        </p>
        <hr className="max-w-[32px] h-px bg-[#F4EADF] border-0 mx-auto opacity-50 md:hidden" />
        <p className="text-[24px] md:text-[32px] italic tracking-[0.32px] leading-tight text-center text-[#f4eadf]">
          {t("home").footer.dataPrivacy}
        </p>

        <div
          className="
            pt-[72px]
            md:hidden
            w-full"
        >
          <img
            src="/assets/iphone-mockup.png"
            alt="iPhone App Preview"
            className="w-full"
          />
        </div>
      </div>

      {showVideo && (
        <Suspense fallback={<div className="fixed inset-0 bg-[#1c1815] bg-opacity-60 z-50 flex items-center justify-center"><div className="text-white">Loading...</div></div>}>
          <VideoOverlay
            playbackId={t("video").muxPlaybackId}
            onClose={() => setShowVideo(false)}
          />
        </Suspense>
      )}
      </main>
    </>
  );
}
